import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity } from '@api/api/entity-schema';
import {
  FinancialStatementType,
  TimeFrame,
} from '@api/api/financial-statements';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useSnakeMeta } from '@terminal/react-query-hooks/snakes/useSnake';
import { queryKeys } from '@terminal/routes/query-params';
import assetOverviewStyles from '@terminal/views/asset-overview/AssetOverview.module.scss';
import { useTranslation } from 'react-i18next';

import { FinancialTable } from './financial-table/FinancialTable';
import { useFinancialStatement } from './use-financial-statement/useFinancialStatement';

export interface AssetFinancialsProps {
  entity: Entity;
}

const timeFrames: TimeFrame[] = ['annual', 'quarterly'];
// order matters here
const financialStatementButtonGroupLabels: FinancialStatementType[] = [
  'incomeStatement',
  'balanceSheet',
  'cashFlow',
];

export const AssetFinancialsWidget = ({ entity }: AssetFinancialsProps) => {
  const { t } = useTranslation('widget');

  const { params, setQueryParams } = useQueryParams();

  const activeFinancialsStatement = (params[
    queryKeys.activeFinancialsStatement
  ] || financialStatementButtonGroupLabels[0]) as FinancialStatementType;
  const activeFinancialsTimeframe = (params[
    queryKeys.activeFinancialsTimeframe
  ] || timeFrames[0]) as TimeFrame;
  const { data, isFetching } = useFinancialStatement({
    entityTag: entity.tag,
    type: activeFinancialsStatement,
    timeframe: activeFinancialsTimeframe,
  });

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const { data: ibesData } = useSnakeMeta(
    isStock ? `${entity.tag}.ibes_eps_total_number_estimates` : ''
  );

  const activeStatementButtonGroupIndex =
    financialStatementButtonGroupLabels.indexOf(activeFinancialsStatement);

  const handleStatementButtonGroupSelection = (i: number) => {
    setQueryParams({
      [queryKeys.activeFinancialsStatement]:
        financialStatementButtonGroupLabels[i],
    });
  };

  const activeTimeframeButtonGroupIndex = timeFrames.indexOf(
    activeFinancialsTimeframe
  );

  const handleTimeframeButtonGroupSelection = (i: number) => {
    setQueryParams({ [queryKeys.activeFinancialsTimeframe]: timeFrames[i] });
  };

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              maxWidth: 'var(--content-max-width)',
              borderRadius: '8px',
              height: '360px',
              margin: '20px auto',
            },
          },
        ]}
        data-testid="asset-financials-loader"
      />
    );
  }

  if (!data) {
    return (
      <ErrorMessageWrapper
        className={assetOverviewStyles.errorWrapper}
        variant="empty"
        displayIcon
        title={t('widget:financials.empty.title')}
        subTitle={t('widget:financials.empty.subTitle', { asset: entity.name })}
        data-testid="asset-financials-error-message"
      />
    );
  }

  const analystEpsEstimatesLabel =
    ibesData && !!ibesData.last_value
      ? t('widget:assetOverview.analystEpsEstimates', {
          count: ibesData.last_value,
        })
      : undefined;

  return (
    <FinancialTable
      data={data}
      activeStatementButtonGroupIndex={activeStatementButtonGroupIndex}
      handleStatementButtonGroupSelection={handleStatementButtonGroupSelection}
      activeTimeframeButtonGroupIndex={activeTimeframeButtonGroupIndex}
      handleTimeframeButtonGroupSelection={handleTimeframeButtonGroupSelection}
      analystEpsEstimatesLabel={analystEpsEstimatesLabel}
    />
  );
};
